// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable @typescript-eslint/no-unnecessary-condition */ // eslint-disable-next-line eslint-comments/disable-enable-pair
// TODO: Add types for mixpanel and IA: https://lastbrand.atlassian.net/browse/SFPLTFRM-873
import 'client-only'

import { EVENT_DELAY } from '@src/config/constants'
import { CONFIG_DEBUG_EVENT_LOGGING, CONFIG_INTERNAL_ANALYTICS_V2_ENDPOINT } from '@src/config/env'
import { DEFAULT_LOCALE, LOCALE_COOKIE } from '@src/config/localisation/locale-config.static-constants'
import { getFingerprintVisitorDetails } from '@src/modules/fingerprint/fingerprint'
import { getIsCountryCompliant } from '@src/modules/global-state/locale-config.browser'
import { getUserAgnosticGlobalFeatureGatesFromMemory } from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import { Filter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { DOB_MODAL_EVENT_ENTITY_TYPE } from '@src/quince-components/src/atoms/dobModal/constants'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import QuinceApiError from '@src/quince-site-common/src/domains/quince-api-error'
import appData from '@src/quince-site-common/src/helpers/appData'
import { getCartIDAndAuth, getCheckoutToken } from '@src/quince-site-common/src/hooks/cart/hooks/new-cart-helper'
import { getZipCodeFromCookie } from '@src/quince-site-common/src/hooks/zipcode/utils/zipcode.utils'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { B2B_EVENT_ENTITY_TYPE } from '@src/quince-site-common/src/utils/b2b/constants'
import { ANALYTIC_COOKIES, USER_CONSENT_COOKIES } from '@src/quince-site-common/src/utils/constants'
import { getAppAnalyticsData } from '@src/quince-site-common/src/utils/cross-platform-analytics/cross-platform-analytics.utils'
import getUtmParameters from '@src/quince-site-common/src/utils/get-utm-parameters'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { getPageContextDetails } from '@src/quince-site-common/src/utils/getPageContextDetails'
import { getProductTagsDataForBatchedAnalyticsFromTags } from '@src/quince-site-common/src/utils/getProductTagsDataForBatchedAnalytics'
import getUserDevice from '@src/quince-site-common/src/utils/getUserDevice'
import getUserDeviceCategory from '@src/quince-site-common/src/utils/getUserDeviceCategory'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import setCookie from '@src/quince-site-common/src/utils/setCookie'
import validateEmail from '@src/quince-site-common/src/utils/validateEmail'
import { getPaymentAttempt, updatePaymentAttempt } from '@src/telemetry/checkout-attempts'
import mixpanelCl from '@src/telemetry/integrations/mixpanel'
import { TelemetryIntegration, TelemetryEventDataMap } from '@src/telemetry/telemetry-events.types'
import { trackWithSgtm } from '@src/telemetry/trackWithSgtm'
import { getStorefrontIdProperty } from '@src/utils/b2b'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'
import {
  getLocalisationDataFromStorage,
  getLocalisationDataWithFallbacks,
  transformTagsIntoAnalyticsTags,
} from '@src/utils/general.utils'
import { getSessionId } from '@src/utils/user/session.browser'

import { TELEMETRY_EVENT_NAMES } from '../telemetry-events.constants'

import { getActiveExperiments } from './internal-analytics.utils'
import { getB2BProperties } from './mixpanel/mixpanel.helper'
import { validateLbUserId, validateSessionId } from './provider-utils/internal-analytics-utils'
import { getSafeLocationHref } from './provider-utils/safe-location-utils'

declare global {
  interface Window {
    __unique_path_hash__: string
    __client_side_navigation__: boolean
  }
}

let pageHash = ''

const UNKNOWN_ERROR_MESSAGE = 'Unknown error'

const normalizeErrorForTelemetry = (error: unknown) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    }
  }

  if (typeof error === 'string') {
    return { message: error }
  }

  if (error && typeof error === 'object') {
    return error
  }

  return { message: UNKNOWN_ERROR_MESSAGE, value: String(error) }
}

export const InternalAnalytics: TelemetryIntegration = {
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
    skipConsentCheck: true,
    featureGateName: 'ssr_telemetry_internal_analytics',
  },

  events: {
    [TELEMETRY_EVENT_NAMES.COOKIE_PREFERENCE_SAVED]: properties => {
      logSessionStreamEvent({
        event_type: 'cookie_preference_saved',
        entity_type: 'cookie_consent',
        context: properties.context,
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.COOKIE_SETTINGS_OPENED]: properties => {
      logSessionStreamEvent({
        event_type: 'cookie_settings_opened',
        entity_type: 'cookie_consent',
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.COOKIE_BANNER_SHOWN]: properties => {
      logSessionStreamEvent({
        event_type: 'cookie_banner_shown',
        entity_type: 'cookie_consent',
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.SESSION_CREATE]: properties => {
      const context = {
        lb_user_id: getlbUserId(),
        email: getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL),
        device_type: getUserDevice(),
        last_session_id: properties.lastSessionId,
      }

      logSessionStreamEvent({
        event_type: 'session_create',
        entity_type: 'session_id',
        entity_id: properties.sessionId,
        context,
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.VIDEO_PLAYED]: properties => {
      const context = {
        ...properties.context,
        ...(properties.context?.variant_id != null && { variant_id: properties.context.variant_id }),
        ...(properties.context?.video_id != null && { video_id: properties.context.video_id }),
      }
      logSessionStreamEvent({
        event_type: 'video_played',
        entity_type: 'action',
        entity_id: properties.tracker ?? 'video',
        context,
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.VIDEO_COMPLETED]: properties => {
      const context = {
        ...properties.context,
        ...(properties.context?.variant_id != null && { variant_id: properties.context.variant_id }),
        ...(properties.context?.video_id != null && { video_id: properties.context.video_id }),
      }
      logSessionStreamEvent({
        event_type: 'video_completed',
        entity_type: 'action',
        entity_id: properties.tracker ?? 'video',
        context,
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.APP_PUSH_BANNER_VIEWED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'app_push_banner_viewed',
        eventId: properties.eventId,
        entity_type: 'navigation',
        entity_id: 'app_banner_view',
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.NEW_USER_CREATED_RANDOM]: properties => {
      const context = {
        lb_user_id: getlbUserId(),
        email: getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL),
        device_type: getUserDevice(),
      }

      logSessionStreamEvent({
        event_type: 'new_user_created_random',
        entity_type: 'new_user_created_random_event',
        entity_id: getSessionId(),
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.PRODUCT_VIEW]: properties => {
      if (!pageHash || pageHash != window.__unique_path_hash__) {
        pageHash = window.__unique_path_hash__
        const context = {
          ...(properties?.context ?? {}),
          product_id: properties.productId,
          isValentineProduct: properties.isValentineProduct,
          name: properties.productTitle,
          division: properties.division,
          subDepartment: properties.subDepartment,
          department: properties.department,
          gender: properties.gender,
        }

        logSessionStreamEvent({
          event_type: 'product_view',
          entity_type: 'product_variant',
          entity_id: properties.variantId,
          context,
          eventId: properties.eventId,
        })
        return
      }
      pageHash = window.__unique_path_hash__
    },

    [TELEMETRY_EVENT_NAMES.ADD_TO_CART]: properties => {
      const { cartID, authCode } = getCartIDAndAuth()
      const context = {
        ...(properties?.context ?? {}),
        product_id: properties.productId,
        line_items: properties.lineItems,
        tracker_section: properties.trackerSection,
        quantity: properties.quantity,
        checkout_token: getCheckoutToken(),
        is_preorder: properties.isPreorder,
        product_search_query: properties.productSearchQuery,
        product_position: properties.product_position,
        source: properties.inventoryInfoSource,
        division: properties.division,
        department: properties.department,
        subDepartment: properties.subDepartment,
        price: properties.price,
        // We get cart_upsell_${trackerConfigKey} as trackerSection for mobile view quick shop modal.

        quick_add_to_cart:
          properties?.trackerSection === 'quick_shop' ||
          properties?.trackerSection === 'social_section' ||
          properties?.trackerSection?.includes('cart_upsell_')
            ? 1
            : 0,
        color_variant: properties.color_variant,
        gender: properties.gender,
        cartID: cartID,
        authCode: authCode,
        productProperties: {
          ...properties.productProperties,
          price: properties.price,
          is_preorder:
            properties.productProperties?.is_preorder !== undefined
              ? properties.productProperties.is_preorder
              : Boolean(properties.isPreorder),
          preorder_edd: properties.productProperties?.preorder_edd,
        },
        color_swatch_position: properties.color_swatch_position,
        is_customization_added: properties?.isCustomizationAdded,
        variant_quantity_map: properties?.variantQuantityMap,
        value: properties?.value,
      }

      logSessionStreamEvent({
        event_type: properties.isFromPayClickPdp ? 'atc_from_pay_click_pdp' : 'add_to_cart',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.BUY_NOW]: (properties: any) => {
      const { cartID, authCode } = getCartIDAndAuth()
      const context = {
        ...properties?.context,
        product_id: properties.productId,
        line_items: (properties.lineItems || []).map((li: any) => ({
          ...li,
          productId: li?.productId?.toString(),
          variantId: li?.variantId?.toString(),
          price: li?.price?.toString(),
        })),
        tracker_section: properties.trackerSection,
        quantity: properties.quantity,
        checkout_token: getCheckoutToken(),
        is_preorder: properties.isPreorder,
        product_search_query: properties.productSearchQuery,
        product_position: properties.product_position,
        source: properties.inventoryInfoSource,
        division: properties.division,
        department: properties.department,
        subDepartment: properties.subDepartment,
        color_variant: properties.color_variant,
        gender: properties.gender,
        cartID: cartID,
        authCode: authCode,
        productProperties: properties.productProperties,
      }
      logSessionStreamEvent({
        event_type: 'buy_now',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.APPLE_PAY_CLICK]: (properties: any) => {
      const context = {
        ...properties?.context,
        product_id: properties.productId,
        line_items: (properties.lineItems || []).map((li: any) => ({
          ...li,
          productId: li?.productId?.toString(),
          variantId: li?.variantId?.toString(),
          price: li?.price?.toString(),
        })),
        tracker_section: properties.trackerSection,
        quantity: properties.quantity,
        checkout_token: getCheckoutToken(),
        is_preorder: properties.isPreorder,
        product_search_query: properties.productSearchQuery,
        product_position: properties.productPosition,
        source: properties.inventoryInfoSource,
        division: properties.division,
        department: properties.department,
        subDepartment: properties.subDepartment,
        color_variant: properties.color_variant,
        gender: properties.gender,
        // cartID: cartID,
        // authCode: authCode,
        productProperties: properties.productProperties,
      }
      logSessionStreamEvent({
        event_type: 'apple_pay_clicked',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.APPLE_PAY_PAYMENT_INITIATED]: properties => {
      const context = {
        checkoutId: properties.checkoutId,
        sessionId: properties.sessionId,
        amountToCollect: properties.amountToCollect,
      }
      logSessionStreamEvent({
        event_type: 'apple_pay_payment_initiated',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.UNIFIED_PAY_BUTTON_CLICK]: properties => {
      const attempt = getPaymentAttempt()
      const context = {
        checkout_pay_button_click: true,
        payment_init_token: attempt?.token,
        payment_method: properties.paymentMethod,
        flow: properties.flow,
        ...(properties.errorReason ? { error_reason: properties.errorReason } : {}),
      }
      logSessionStreamEvent({
        event_type: 'checkout_action',
        entity_type: properties.flow,
        entity_id: properties.paymentMethod,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.UNIFIED_PAYMENT_INITIATED]: properties => {
      updatePaymentAttempt({ payment_gateway: properties.paymentGateway })
      const attempt = getPaymentAttempt()
      if (!attempt) return
      logSessionStreamEvent({
        event_type: 'payment_initiated_web_checkout',
        entity_type: attempt.flow,
        entity_id: attempt.payment_method,
        context: {
          payment_init_token: attempt.token,
          payment_gateway: properties.paymentGateway,
          payment_method: attempt.payment_method,
          flow: attempt.flow,
        },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.UNIFIED_PAYMENT_AUTHORISED]: properties => {
      const attempt = getPaymentAttempt()
      if (!attempt) return
      logSessionStreamEvent({
        event_type: 'payment_authorised',
        entity_type: attempt.flow,
        entity_id: attempt.payment_method,
        context: {
          payment_init_token: attempt.token,
          payment_gateway: attempt.payment_gateway,
          payment_method: attempt.payment_method,
          flow: attempt.flow,
        },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.APPLE_PAY_PAYMENT_FAILED]: properties => {
      const context = {
        error: normalizeErrorForTelemetry(properties.error),
      }
      logSessionStreamEvent({
        event_type: 'apple_pay_payment_failed',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.CHECKOUT_ERROR_LOG_WEB_CHECKOUT]: properties => {
      const attempt = getPaymentAttempt()
      if (!attempt) return
      logSessionStreamEvent({
        event_type: 'checkout_error_log_web_checkout',
        entity_type: attempt.flow,
        entity_id: attempt.payment_method,
        context: {
          payment_init_token: attempt.token,
          payment_gateway: attempt.payment_gateway,
          payment_method: attempt.payment_method,
          flow: attempt.flow,
          ...(properties.errorReason ? { error_reason: properties.errorReason } : {}),
          ...(properties.errorType ? { error_type: properties.errorType } : {}),
          ...(properties.errorStage ? { error_stage: properties.errorStage } : {}),
          ...(properties.errorOrigin ? { error_origin: properties.errorOrigin } : {}),
          ...(properties.errorMessage ? { error_message: properties.errorMessage } : {}),
          ...(typeof properties.httpStatus === 'number' ? { http_status: properties.httpStatus } : {}),
          ...(properties.errorCode ? { error_code: properties.errorCode } : {}),
          ...(properties.apiEndpoint ? { api_endpoint: properties.apiEndpoint } : {}),
        },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.REMOVE_FROM_CART]: properties => {
      const { cartID, authCode } = getCartIDAndAuth()
      const context = {
        product_id: properties.productId,
        line_items: properties.lineItems,
        checkout_token: getCheckoutToken(),
        cartID: cartID,
        authCode: authCode,
        quantity: properties.quantity,
        is_modal: properties.isModal ?? false,
        productProperties: properties.productProperties,
      }

      logSessionStreamEvent({
        event_type: 'remove_from_cart',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SAVE_FOR_LATER]: properties => {
      const { cartID, authCode } = getCartIDAndAuth()
      const context = {
        product_id: properties.productId,
        line_items: properties.lineItems,
        checkout_token: getCheckoutToken(),
        cartID: cartID,
        authCode: authCode,
        quantity: properties.quantity,
        is_modal: properties.isModal ?? false,
        productProperties: properties.productProperties,
      }

      logSessionStreamEvent({
        event_type: 'save_for_later',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.MOVE_TO_BAG]: properties => {
      const { cartID, authCode } = getCartIDAndAuth()
      const context = {
        product_id: properties.productId,
        line_items: properties.lineItems,
        checkout_token: getCheckoutToken(),
        cartID: cartID,
        authCode: authCode,
        quantity: properties.quantity,
        is_modal: properties.isModal ?? false,
        productProperties: properties.productProperties,
      }

      logSessionStreamEvent({
        event_type: 'move_to_bag',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.EMAIL_NEWSLETTER_SUBSCRIBE]: properties => {
      const context = {
        ...(properties?.context ?? {}),
        email: properties.email,
        lb_user_id: getlbUserId(),
      }
      captureTentativeEmail(properties.email)
      if (properties.shouldSendWelcomeMail) {
        logSessionStreamEvent({
          event_type: 'klaviyo_newsletter_subscribe',
          entity_type: 'email',
          entity_id: properties.email,
          context,
          eventId: properties.eventId,
        })
      }
    },

    [TELEMETRY_EVENT_NAMES.SMS_NEWSLETTER_SUBSCRIBE]: properties => {
      if (properties.shouldSendWelcomeMail) {
        logSessionStreamEvent({
          event_type: 'sms_newsletter_subscribe',
          entity_type: 'phone',
          entity_id: properties.mobileNumber,
          context: {},
          eventId: properties.eventId,
        })
      }
    },

    [TELEMETRY_EVENT_NAMES.UPDATE_ITEM_QUANTITY]: properties => {
      // B2B self-serve emits line-item scoped quantity events (lineItemId branch).
      // D2C uses the variant/cart payload branch below (variantId + lineItems).
      if ('lineItemId' in properties && properties.lineItemId) {
        logSessionStreamEvent({
          event_type: 'update_item_quantity',
          entity_type: 'line_item',
          entity_id: properties.lineItemId,
          eventId: properties.eventId,
          context: {
            cartID: getCartIDAndAuth().cartID,
            product_quantity_changed_cart: properties.productQuantityChangedCart,
            ...(properties.movementDetails && { movement_details: properties.movementDetails }),
            ...(properties.variantId != null ? { variant_id: String(properties.variantId) } : {}),
            ...(properties.productId != null ? { product_id: String(properties.productId) } : {}),
            ...(properties.groupId && { group_id: properties.groupId }),
            ...(properties.batchUpdateDetails?.length
              ? {
                  batch_update_details: properties.batchUpdateDetails.map(item => ({
                    line_item_id: item.lineItemId,
                    ...(item.movementDetails && { movement_details: item.movementDetails }),
                    ...(item.variantId != null ? { variant_id: String(item.variantId) } : {}),
                    ...(item.productId != null ? { product_id: String(item.productId) } : {}),
                    ...(item.groupId ? { group_id: item.groupId } : {}),
                  })),
                }
              : {}),
          },
        })
        return
      }
      const cartQuantityProps = properties as Extract<
        TelemetryEventDataMap[typeof TELEMETRY_EVENT_NAMES.UPDATE_ITEM_QUANTITY],
        { variantId: string }
      >
      const { cartID, authCode } = getCartIDAndAuth()
      const context = {
        product_id: cartQuantityProps.productId,
        line_items: cartQuantityProps.lineItems,
        checkout_token: getCheckoutToken(),
        cartID: cartID,
        authCode: authCode,
        movement_details: cartQuantityProps.movementDetails,
        is_modal: cartQuantityProps.isModal ?? false,
      }
      logSessionStreamEvent({
        event_type: 'update_item_quantity',
        entity_type: 'product_variant',
        entity_id: cartQuantityProps.variantId,
        context,
        eventId: cartQuantityProps.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.DIRECT_BUY_CLICK]: properties => {
      const context = {
        product_id: properties.productId,
        tracker_section: properties.trackerSection,
      }
      logSessionStreamEvent({
        event_type: 'direct_buy_click',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.PAGE_SCROLL]: properties => {
      logSessionStreamEvent({
        event_type: 'page_scroll',
        entity_type: 'scroll_percentage',
        entity_id: properties.scrollPercentage,
        context: {
          is_modal: properties.isModal ?? false,
        },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.ZOOM_PRODUCT_IMAGE]: properties => {
      const context = {
        productId: properties.productId,
        ...(properties?.context ?? {}),
      }
      //TODO : We dont currently get variant id here, need to finalize if we want at variant level or product level is suffice
      logSessionStreamEvent({
        event_type: 'product_thumbnail_zoom',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.JOIN_THE_WAITLIST]: properties => {
      const context = {
        productId: properties.productId,
        email: properties.email,
        lb_user_id: getlbUserId(),
        ...(properties?.context ?? {}),
      }
      captureTentativeEmail(properties.email)
      logSessionStreamEvent({
        event_type: 'join_the_waitlist',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.JOIN_THE_WAITLIST_SMS]: properties => {
      const context = {
        productId: properties.productId,
        email: properties.email,
        phone_number: properties.phoneNumber,
        lb_user_id: getlbUserId(),
        ...(properties?.context ?? {}),
      }
      logSessionStreamEvent({
        event_type: 'join_the_waitlist_sms',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.PAGE_VIEW]: properties => {
      let safePathname = properties.pathname
      if (!safePathname) {
        try {
          safePathname = window?.location?.pathname || ''
        } catch {
          safePathname = ''
        }
      }
      logSessionStreamEvent({
        event_type: 'page_view',
        entity_type: 'page_path',
        entity_id: safePathname,
        context: {
          ...properties.context,
          collection_id: properties.collection_id,
          collection_name: properties.collection_name,
          sort_model: properties.sort_model,
        },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.HOME_PAGE_VIEW]: properties => {
      let safePathname = properties.pathname
      if (!safePathname) {
        try {
          safePathname = window?.location?.pathname || ''
        } catch {
          safePathname = ''
        }
      }
      logSessionStreamEvent({
        event_type: 'home_page_view',
        entity_type: 'page_path',
        entity_id: safePathname,
        context: properties.context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.WISHLIST_PAGE_VIEW]: properties => {
      let safePathname = properties.pathname
      if (!safePathname) {
        try {
          safePathname = window?.location?.pathname || ''
        } catch {
          safePathname = ''
        }
      }
      logSessionStreamEvent({
        event_type: 'wishlist_page_view',
        entity_type: 'page_path',
        entity_id: safePathname,
        context: properties.context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SEARCH_PAGE_VIEW]: properties => {
      let safePathname = properties.pathname
      if (!safePathname) {
        try {
          safePathname = window?.location?.pathname || ''
        } catch {
          safePathname = ''
        }
      }
      logSessionStreamEvent({
        event_type: 'search_page_view',
        entity_type: 'page_path',
        entity_id: safePathname,
        context: properties.context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.CART_PAGE_VIEW]: properties => {
      let safePathname = properties.pathname
      if (!safePathname) {
        try {
          safePathname = window?.location?.pathname || ''
        } catch {
          safePathname = ''
        }
      }
      logSessionStreamEvent({
        event_type: 'cart_page_view',
        entity_type: 'page_path',
        entity_id: safePathname,
        context: {
          ...(properties.context ?? {}),
          is_modal: properties.isModal ?? false,
          cartID: getCartIDAndAuth().cartID,
        },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.CART_PAGE_ACTION]: properties => {
      logSessionStreamEvent({
        event_type: 'cart_page_action',
        entity_type: properties.entity_type,
        entity_id: 'cart_page_action',
        context: { ...properties, cartID: getCartIDAndAuth().cartID },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.CART_ITEMS_FETCHED]: properties => {
      logSessionStreamEvent({
        event_type: 'cart_items_fetched',
        entity_type: 'action',
        entity_id: 'cart_items_fetched',
        context: { ...properties, cartID: getCartIDAndAuth().cartID },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.CART_REGION_ID_MISMATCH]: properties => {
      logSessionStreamEvent({
        event_type: 'cart_region_id_mismatch',
        entity_type: 'cart_id',
        entity_id: properties.cart_id ?? 'unknown',
        context: properties,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.COLLECTION_VIEW]: properties => {
      const variantLocationFunction =
        getUserDevice() === 'mobile' ? getVariantLocationInCollectionOnMobile : getVariantLocationInCollectionOnDesktop
      const context = {
        collection_id: properties?.collection_id,
        category_slug: properties.categorySlug,
        category_name: properties.categoryName,
        collection_slug: properties.collectionSlug,
        collection_name: properties?.collection_name,
        subcollection_slug: properties.subcollectionSlug,
        subcollection_name: properties.subcollectionName,
        zipCode: properties.zipCode ?? null,
        sort_model: properties.sort_model,
        variant_list: properties?.variantList.map((variant_info: any) => {
          return {
            product_id: variant_info.product_id,
            variant_id: variant_info.variant_id,
            price: variant_info.price,
            labels: variant_info.labels,
            ...variantLocationFunction(variant_info.index),
          }
        }),
        filter_list: properties?.filterList?.filter((ft: Filter) => ft.values.length),
        isDynamicCollectionPage: properties.isDynamicCollectionPage,
      }
      const collectionName = properties.subcollectionName ? properties.subcollectionName : properties.collection_name
      logSessionStreamEvent({
        event_type: 'collection_view',
        entity_type: 'collection_name',
        entity_id: collectionName,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SEND_REFERRAL_EMAIL_INVITE]: properties => {
      const context = {
        email: properties.email,
        lb_user_id: getlbUserId(),
      }
      captureTentativeEmail(properties.email)
      logSessionStreamEvent({
        event_type: 'send_referral_email_invite',
        entity_type: 'email',
        entity_id: properties.targetEmail,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.REFERRAL_URL_LANDING]: properties => {
      logSessionStreamEvent({
        event_type: 'referral_url_landing',
        entity_type: 'action',
        context: {
          source: properties.source,
          param: properties.param,
          pathname: properties.pathname,
          traffic_source: properties.trafficSource,
        },
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.REFERRAL_POPUP_OPENED]: properties => {
      logSessionStreamEvent({
        event_type: 'referral_popup_opened',
        entity_type: 'action',
        context: {
          source: properties.source,
          popup_type: properties.popupType,
        },
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.REFERRAL_EMAIL_INVITE_GENERATED]: properties => {
      logSessionStreamEvent({
        event_type: 'referral_email_invite_generated',
        entity_type: 'action',
        context: {
          source: properties.source,
          has_code: properties.hasCode,
        },
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.REFERRAL_COUPON_GENERATED]: properties => {
      logSessionStreamEvent({
        event_type: 'referral_coupon_generated',
        entity_type: 'action',
        context: {
          source: properties.source,
          has_code: properties.hasCode,
        },
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.REFERRAL_REDEMPTION_POPUP_OPENED]: properties => {
      logSessionStreamEvent({
        event_type: 'referral_redemption_popup_opened',
        entity_type: 'action',
        context: {
          source: properties.source,
        },
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.REFERRAL_URL_LANDING_ERROR]: properties => {
      logSessionStreamEvent({
        event_type: 'referral_url_landing_error',
        entity_type: 'action',
        context: {
          source: properties.source,
          message: properties.message,
        },
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.REFERRAL_POPUP_OPEN_ERROR]: properties => {
      logSessionStreamEvent({
        event_type: 'referral_popup_open_error',
        entity_type: 'action',
        context: {
          source: properties.source,
          message: properties.message,
        },
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.REFERRAL_EMAIL_INVITE_ERROR]: properties => {
      logSessionStreamEvent({
        event_type: 'referral_email_invite_error',
        entity_type: 'action',
        context: {
          source: properties.source,
          message: properties.message,
        },
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.REFERRAL_COUPON_GENERATION_ERROR]: properties => {
      logSessionStreamEvent({
        event_type: 'referral_coupon_generation_error',
        entity_type: 'action',
        context: {
          source: properties.source,
          message: properties.message,
        },
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.REFERRAL_REDEMPTION_POPUP_OPEN_ERROR]: properties => {
      logSessionStreamEvent({
        event_type: 'referral_redemption_popup_open_error',
        entity_type: 'action',
        context: {
          source: properties.source,
          message: properties.message,
        },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.ACCORDION_TOGGLE]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'accordion_toggle',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.BUTTON_CLICK]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
        productProperties: properties?.context?.productProperties,
        ...getB2BProperties(
          properties.context?.isB2BFlow,
          properties.context?.isCompanyFunded,
          properties.context?.totalPrice,
        ),
        ...(properties.context?.isB2BFlow && properties.action === 'start_checkout'
          ? {
              // eslint-disable-next-line i18next/no-literal-string
              intent: 'B2B_BULK',
              ...(properties.context?.totalPrice !== undefined ? { cart_value: properties.context.totalPrice } : {}),
            }
          : {}),
      }

      //override default context values
      if (properties.context?.label) {
        context.label = properties.context.label
      }

      logSessionStreamEvent({
        event_type: 'button_click',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.LINK_CLICK]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        category_name: properties.context?.label ? [properties.context.label] : undefined,
        category_position:
          properties.context?.col !== undefined && properties.context.row !== undefined
            ? [properties.context.col * 2 + (properties.context.row + 1)]
            : undefined,
        label: properties.label,
        value: properties.value,
      }

      //override default context values
      if (properties.context?.label) {
        context.label = properties.context.label
      }

      logSessionStreamEvent({
        event_type: 'link_click',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.FORM_SUBMIT]: properties => {
      if (properties.action === 'account_login' || properties.action === 'account_create') {
        captureTentativeEmail(properties.context.login_email, false)
      }
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
        action: properties.action,
      }
      logSessionStreamEvent({
        event_type: 'form_submit',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.FORM_OPEN]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'form_open',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.FORM_CLOSE]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'form_close',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.MODAL_OPEN]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'modal_open',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.MODAL_CLOSE]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'modal_close',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.ATC_CONFIRMATION_BOTTOM_SHEET_VIEW]: properties => {
      logSessionStreamEvent({
        event_type: 'atc_confirmation_bottom_sheet_view',
        entity_type: 'cart',
        entity_id: 'atc_confirmation_bottom_sheet',
        context: properties,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.ATC_CONFIRMATION_BOTTOM_SHEET_DISMISS]: properties => {
      logSessionStreamEvent({
        event_type: 'atc_confirmation_bottom_sheet_dismiss',
        entity_type: 'cart',
        entity_id: 'atc_confirmation_bottom_sheet',
        context: properties,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.ENTER_FEEDBACK]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'enter_feedback',
        entity_type: properties.entity_type,
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SITE_ERROR]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'site_error',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.ADD_TO_WISHLIST]: properties => {
      const context = {
        ...properties.context,
        product_id: properties.productId,
        color: properties.color,
        size: properties.size,
        line_items: properties.lineItems,
        tracker_section: properties.trackerSection,
        product_search_query: properties.product_search_query,
        product_position: properties.product_position,
        product_tags: properties.tags?.map((tag: ProductTag) => tag.value),
        count_color_variant: properties?.count_color_variant,
        department: properties?.department,
        department_id: properties?.department_id,
        division: properties?.division,
        division_id: properties?.division_id,
        rating: properties?.rating,
        rating_count: properties?.rating_count,
        subdepartment: properties?.subdepartment,
        subdepartment_id: properties?.subdepartment_id,
        title: properties?.title,
      }
      logSessionStreamEvent({
        event_type: 'add_to_wishlist',
        entity_type: 'product',
        entity_id: properties.productId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.WISHLIST_LOAD]: properties => {
      const context = {
        wishlist_product_list: properties.wishlistProductList,
      }
      logSessionStreamEvent({
        event_type: 'wishlist_loaded',
        entity_type: 'product',
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SHARED_WISHLIST_LOAD]: properties => {
      const context = {
        wishlist_product_list: properties.wishlistProductList,
      }
      logSessionStreamEvent({
        event_type: 'shared_wishlist_loaded',
        entity_type: 'product',
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SHARE_WISHLIST]: properties => {
      const context = {
        wishlist_product_list: properties.wishlistProductList,
        wishlist_token: properties.wishlistToken,
      }
      logSessionStreamEvent({
        event_type: 'share_wishlist',
        entity_type: properties.wishlistToken,
        entity_id: properties.wishlistToken,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.REMOVE_FROM_WISHLIST]: properties => {
      const context = {
        ...properties.context,
        product_id: properties.productId,
        color: properties.color,
        size: properties.size,
        line_items: properties.lineItems,
        tracker_section: properties.trackerSection,
        product_tags: properties.tags?.map((tag: ProductTag) => tag.value),
        product_tags_transformed: transformTagsIntoAnalyticsTags(
          properties.tags?.map((tag: ProductTag) => tag.value) ?? [],
        ),
      }
      logSessionStreamEvent({
        event_type: 'remove_from_wishlist',
        entity_type: 'product',
        entity_id: properties.productId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.VIDEO_LOAD]: properties => {
      const context = {
        productId: properties.productId,
        color: properties.color,
        ...properties.context,
        ...(properties.context?.variant_id != null && { variant_id: properties.context.variant_id }),
        ...(properties.context?.video_id != null && { video_id: properties.context.video_id }),
      }
      logSessionStreamEvent({
        event_type: 'load_video',
        entity_type: 'product',
        entity_id: properties.productId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.STREAMING_VIDEO_LOAD]: properties => {
      const context = {
        ...properties.context,
      }
      logSessionStreamEvent({
        event_type: 'load_streaming_video',
        entity_type: 'action',
        entity_id: 'homepage',
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.STREAMING_VIDEO_ERROR]: properties => {
      const context = {
        ...properties.context,
      }
      logSessionStreamEvent({
        event_type: 'error_streaming_video',
        entity_type: 'action',
        entity_id: 'homepage',
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.FILTER_CLICK]: properties => {
      const context = {
        ...properties.context,
        label: properties.label,
        value: properties.value,
      }

      //override default context values
      if (properties.context.label) {
        context.label = properties.context.label
      }

      logSessionStreamEvent({
        event_type: 'filter_click',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.VIEW_BAG_BUTTON_CLICKED]: properties => {
      logSessionStreamEvent({
        event_type: 'view_bag_button_clicked',
        entity_type: 'action',
        context: { is_modal: properties.is_modal },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.CHECKOUT_BUTTON_CLICKED]: properties => {
      logSessionStreamEvent({
        event_type: 'checkout_button_clicked',
        entity_type: 'action',
        context: { is_modal: properties.is_modal },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.API_RESPONSE]: properties => {
      const context = {
        ...properties.context,
        status: properties.status,
        inputRequest: properties.inputRequest,
        response: properties.response,
      }
      logSessionStreamEvent({
        event_type: 'api_response',
        entity_type: 'api_name',
        entity_id: properties.apiName,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.ORDER_CANCEL]: properties => {
      const context = {
        ...properties.context,
      }
      logSessionStreamEvent({
        event_type: 'order_cancel',
        entity_type: 'order_id',
        entity_id: properties.entityId,
        context,
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.PRODUCT_CLICK]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      //override default context values
      if (properties.context.label) {
        context.label = properties.context.label
      }

      logSessionStreamEvent({
        event_type: 'product_click',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.VISUAL_CUE_CLICK]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'visual_cue_click',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.IS_VISIBLE]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'is_visible',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.EXPRESS_CHECKOUT_CART_LOADED]: properties => {
      const context = {
        ...properties,
        cartID: getCartIDAndAuth().cartID,
      }
      logSessionStreamEvent({
        event_type: 'express_checkout_cart_loaded',
        entity_type: 'action',
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.EXPRESS_CHECKOUT_CART_LOAD_FAILED]: properties => {
      const context = {
        ...properties,
        cartID: getCartIDAndAuth().cartID,
      }
      logSessionStreamEvent({
        event_type: 'express_checkout_cart_load_failed',
        entity_type: 'action',
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.IS_VIDEO_WATCHED]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
        ...(properties.context?.variant_id != null && { variant_id: properties.context.variant_id }),
        ...(properties.context?.video_id != null && { video_id: properties.context.video_id }),
      }
      logSessionStreamEvent({
        event_type: 'is_video_watched',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.IDME_VERIFICATION]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'idme_verification',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.PREORDER_INFO_CLICK]: properties => {
      logSessionStreamEvent({
        event_type: 'preorder_info_click',
        entity_id: properties.variantId,
        eventId: properties.eventId,
      })
    },

    // temp log tracking for vue data match
    [TELEMETRY_EVENT_NAMES.RECOMMENDATION_VIEW](properties) {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.RECOMMENDATION_VIEW,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.CAROUSEL_LEFT_CLICK](properties) {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.CAROUSEL_LEFT_CLICK,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.CAROUSEL_RIGHT_CLICK](properties) {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.CAROUSEL_RIGHT_CLICK,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SUBCOLLECTION_CARD_CLICK]: properties => {
      const context = {
        subcollection_slug: properties.slug,
        subcollection_title: properties.title,
      }
      logSessionStreamEvent({
        entity_type: 'subcollection_card',
        eventId: properties.eventId,
        event_type: 'subcollection_card_click',
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW]: properties => {
      const { productList, ...remainingProps } = properties
      const updatedProductList = (productList ?? []).map((product, index) => {
        let productData = product
        if (product.productProperties) {
          const { productProperties, ...restProps } = product
          const { product_id: _product_id, ...productPropertiesWithoutId } = productProperties // eslint-disable-line no-console
          productData = {
            ...restProps,
            ...productPropertiesWithoutId,
          }
        }
        return {
          ...productData,
          product_position: productData.product_position ?? { i: index, j: 0 },
        }
      })
      const context = {
        ...remainingProps,
        ...getProductTagsDataForBatchedAnalyticsFromTags(updatedProductList || []),
        collection_slug: properties.collectionSlug,
        products_list: updatedProductList,
        product_count: updatedProductList.length,
        contentContainerType: remainingProps.contentContainerType,
        contentContainerName: properties.contentContainerName,
        contentContainerDisplayName: properties.contentContainerDisplayName,
      }
      logSessionStreamEvent({
        event_type: 'is_visible',
        entity_type: 'product_card_view',
        entity_id: 'visible_product_list',
        context,
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.VARIANT_SELECT]: properties => {
      const context = {
        variantIds: properties.variantIds,
        product_id: properties.product_id,
        internal_variant_ids: properties.internalVariantIds,
        product_options: properties.product_options,
        product_options_all: properties.product_options_all,
        tags: properties.tags,
        tag_ids: transformTagsIntoAnalyticsTags(properties.tags ?? []),
        stock_info: properties.stock_info,
        color: properties.color,
        is_product_card: properties.is_product_card,
        division: properties.division,
        department: properties.department,
        subdepartment: properties.subdepartment,
        product_option_name: properties.product_option_name,
        product_option_value: properties.product_option_value,
        element_name: properties.element_name,
        element_display_name: properties.element_display_name,
        element_position: properties.element_position,
        element_type: properties.element_type,
        element_id: properties.element_id,
        ...(properties?.context ?? {}),
      }
      logSessionStreamEvent({
        event_type: 'variant_select',
        entity_id: properties.entity_id,
        entity_type: properties.entity_type ?? 'variant_id',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.TF_WIDGET_OPEN]: properties => {
      const context = {
        productId: properties.styleId,
        elementId: properties.elementId,
      }
      logSessionStreamEvent({
        event_type: 'tf_modal',
        entity_type: 'modal_open',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.TF_WIDGET_LOAD]: properties => {
      const context = {
        productId: properties.styleId,
        tfSuggestion: properties.successType,
        elementId: properties.elementId,
      }
      logSessionStreamEvent({
        event_type: 'tf_widget_suggestion',
        entity_type: 'widget_suggestion_on_load',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.TF_SIZE_RECOMMENDATION]: properties => {
      const context = {
        productId: properties.styleId,
        size: properties.size,
        elementId: properties.elementId,
      }
      logSessionStreamEvent({
        event_type: 'tf_widget_suggestion',
        entity_type: 'suggestion',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.TF_EXPERIMENTS]: properties => {
      const context = {
        productId: properties.productId,
        colorId: properties.colorId,
        elementId: properties.elementId,
        truefitRegistrationTreatment: properties.truefitRegistrationTreatment,
      }
      logSessionStreamEvent({
        event_type: 'tf_widget_experiments',
        entity_type: 'experiments',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CLICKED_TO_SEARCH]: properties => {
      logSessionStreamEvent({
        event_type: 'clicked_to_search',
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.CUSTOMER_ID_SYNC_ERROR]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'customer_id_sync_failed',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.SEARCH_TRIGGERED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'search_triggered',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.TOP_NAV_OPENED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'top_nav_opened',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.PROFILE_MENU_OPENED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'profile_menu_opened',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.HEADER_LOGO_CLIKED]: properties => {
      logSessionStreamEvent({
        event_type: 'header_logo_clicked',
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.HEADER_GLOBAL_ICON_SELECTED]: properties => {
      const context = {
        eventId: properties.eventId,
        source: properties.source,
        originlocale: properties.originlocale,
        route: properties.route,
      }
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.HEADER_GLOBAL_ICON_SELECTED,
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.LOCALE_SWITCH_MODAL_SAVED]: properties => {
      const context = {
        eventId: properties.eventId,
        source: properties.source,
        originlocale: properties.originlocale,
        destinationLocale: properties.destinationLocale,
        route: properties.route,
        langChanged: properties.langChanged,
        countryChanged: properties.countryChanged,
      }
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.LOCALE_SWITCH_MODAL_SAVED,
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.LOCALE_SWITCH_HEADSUP_POPUP_SELECTED]: properties => {
      const context = {
        eventId: properties.eventId,
        source: properties.source,
        originlocale: properties.originlocale,
        destinationLocale: properties.destinationLocale,
        route: properties.route,
        langChanged: properties.langChanged,
        countryChanged: properties.countryChanged,
      }
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.LOCALE_SWITCH_HEADSUP_POPUP_SELECTED,
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.DIRECT_LOCALE_RADIO]: properties => {
      const context = {
        eventId: properties.eventId,
        route: properties.route,
        langChanged: properties.langChanged,
        countryChanged: properties.countryChanged,
        source: properties.source,
        originlocale: properties.originlocale,
        destinationLocale: properties.destinationLocale,
      }
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.DIRECT_LOCALE_RADIO,
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.LOCALE_SWITCH_MODAL_OPEN]: properties => {
      const context = {
        eventId: properties.eventId,
        source: properties.source,
        originlocale: properties.originlocale,
        route: properties.route,
      }
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.LOCALE_SWITCH_MODAL_OPEN,
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.APP_PUSH_BANNER_INSTALL_CLICKED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'app_push_banner_install_clicked',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.APP_INSTALL_CLICKED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'app_install_clicked',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.SORT_OPENED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'sort_opened',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.SORT_USED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'sort_used',
        entity_id: properties.action,
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.FAQ_VIEWED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'faq_viewed',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.FAQ_CLICKED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'faq_clicked',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.ADDRESS_ADDITION_INITIATED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'address_addition_initiated',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.ADDRESS_ADDITION_SUCCESSFUL]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'address_addition_successful',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.ADDRESS_EDIT_SUCCESSFUL]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'address_edit_successful',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.PRODUCT_GALLERY_VIEWED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'product_gallery_viewed',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.PRODUCT_GALLERY_LOADED]: properties => {
      const context = {
        ...(properties?.context ?? {}),
        product_id: properties.productId,
        name: properties.productTitle,
        division: properties.division,
        subDepartment: properties.subDepartment,
        department: properties.department,
        gender: properties.gender,
      }
      logSessionStreamEvent({
        event_type: 'product_gallery_loaded',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.REVIEWS_SORTED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'reviews_sorted',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.REVIEW_PAGE_NAVIGATED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'review_page_navigated',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.REVIEW_AUTO_SCROLL_TRIGGERED]: properties => {
      const context = properties.context
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.REVIEW_AUTO_SCROLL_TRIGGERED,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.REVIEW_VIEWED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'review_viewed',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_RADIO_BUTTON_SELECTED]: properties => {
      const context = properties.context
      logSessionStreamEvent({
        event_type: 'subscriptions_radio_button_selected',
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_SHIPPING_FREQUENCY_CHANGE]: properties => {
      const context = properties.context
      logSessionStreamEvent({
        event_type: 'subscriptions_shipping_frequency_change',
        eventId: properties.eventId,
        entity_type: properties.entity_type,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.SEARCH_COMPLETED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'search_completed',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.SIMILAR_COLLECTION_CLICKED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'similar_collection_clicked',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.SUBCOLLECTION_FILTER_USED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'subcollection_filter_used',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.VARIANT_SELECTED]: properties => {
      const productProperties = {
        ...properties.productProperties,
        is_preorder:
          properties.productProperties?.is_preorder !== undefined
            ? properties.productProperties.is_preorder
            : typeof properties.is_preorder === 'number'
              ? properties.is_preorder === 1
              : Boolean(properties.is_preorder),
        preorder_edd: properties.productProperties?.preorder_edd,
      }
      const context = {
        ...properties,
        productProperties,
      }
      logSessionStreamEvent({
        event_type: 'variant_selected',
        entity_type: 'product_id',
        entity_id: String(properties.product_id),
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.DO_NOT_TRACK]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'do_not_track',
        eventId: properties.eventId as string,
        entity_type: 'is_tracking_enabled',
        entity_id: context.trackingEnabled as boolean,
      })
    },
    [TELEMETRY_EVENT_NAMES.PRODUCT_GALLERY_SCROLLED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'product_gallery_scrolled',
        eventId: properties.eventId as string,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.PRODUCT_CARD_SCROLLED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'product_card_scrolled',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURN_INIT_STARTED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'return_init_started',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURN_INIT_COMPLETED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'return_init_completed',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURN_INIT_STUCK]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'return_init_stuck',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURN_LINE_ITEMS_SCREEN_VIEW]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'return_line_items_screen_viewed',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURN_FEE_TOOLTIP_HOVER]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'return_fee_tooltip_hover',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.SUBMIT_LINE_ITEMS_CTA_CLICK]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'submit_line_items_cta_clicked',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.REFUND_AMOUNT_SCREEN_VIEW]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'refund_amount_screen_viewed',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.REFUND_AMOUNT_API_COMPLETED]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'refund_amount_api_completed',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.SUBMIT_RETURN_API_COMPLETED]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'submit_return_api_completed',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURN_FLOW_API_ERROR]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'return_flow_api_error',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURN_CONFIRM_CTA_CLICK]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'return_confirm_cta_clicked',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.START_RETURN_CTA_CLICK]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'start_return_cta_click',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURN_CONFIRM_SCREEN_VIEW]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'return_confirm_screen_viewed',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.FILTER_OPENED]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'filter_opened',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURN_ITEM_SELECTED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'return_item_selected',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.EXCHANGE_FOR_NEW_SELECTED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'exchange_for_new_selected',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.PROGRESSIVE_RETURN_FLOW_CONTINUE_CTA]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'progressive_return_flow_continue_cta',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context: {
          ...context,
          ...(properties.failure_reason && { failure_reason: properties.failure_reason }),
        },
      })
    },
    [TELEMETRY_EVENT_NAMES.PROGRESSIVE_RETURN_FLOW_VISIBLE]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'progressive_return_flow_visible',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context: {
          ...context,
          section: {
            ...(properties.reason && { reason: properties.reason }),
            ...(properties.sub_reason && { sub_reason: properties.sub_reason }),
            ...(properties.details && { details: properties.details }),
            ...(properties.evidence && { evidence: properties.evidence }),
          },
        },
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURNLESS_REFUND_MESSAGE_VISIBLE]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'return_less_refund_message_visible',
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.SUPPORT_SECTION_CLICKED]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'support_section_clicked',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURNS_REFUND_TYPE_SELECTED]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'returns_refund_type_selected',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURNS_REFUND_TYPE_SUBMITTED]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'returns_refund_type_submitted',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.ORDERS_LIST_FETCHED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'orders_list_fetched',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.ORDERS_LIST_ERROR]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'orders_list_error',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.PAST_ORDERS_FETCHED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'past_orders_fetched',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.PAST_ORDERS_ERROR]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'past_orders_error',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_LIST_VIEWED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'subscriptions_list_viewed',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_LIST_ERROR]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'subscriptions_list_error',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_PAGE_ACTION]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.SUBSCRIPTIONS_MANAGEMENT_ACTIONS]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CHANGE_NEXT_ORDER_DATE]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CHANGE_SHIPPING_FREQUENCY]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CHANGE_SHIPPING_QUANTITY]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CHANGE_SHIPPING_ADDRESS]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CHANGE_SHIPPING_ADDRESS_ERROR]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CHANGE_NEXT_ORDER_DATE_RESULT]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CHANGE_SHIPPING_FREQUENCY_RESULT]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CHANGE_SHIPPING_QUANTITY_RESULT]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.SUBSCRIPTION_CANCELLED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.PAST_ORDERS_DRAWER_OPENED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.ADDRESS_SELECTOR_OPENED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.ADDRESS_FORM_OPENED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CUSTOMER_ADDRESSES_FETCH_ERROR]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.PAYMENT_CHANGE_INITIATED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.PAYMENT_CHANGE_STATUS]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_id: properties.entity_id,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CHANGE_PAYMENT_METHOD]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETRY_CARD_INITIATED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETRY_CARD_STATUS]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        entity_id: properties.entity_id,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CANCEL_SUBSCRIPTION_BUTTON_CLICK]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CHARGE_BACK_OPTIONS_SURFACED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CONTINUE_TO_CANCEL]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CANCEL_SUBSCRIPTION_CONFIRMATION_CLICK]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: properties.event_type,
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.ORDER_DETAILS_FETCHED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'order_details_fetched',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.ORDER_DETAILS_ERROR]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'order_details_error',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.ORDER_DETAILS_ACTIONS]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'order_details_actions',
        entity_type: properties.entity_type,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.SIZE_CHART_CLICKED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'size_chart_clicked',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.SIZE_CHART_TAB]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.SIZE_CHART_TAB,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.SIZE_CHART_TOGGLE]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.SIZE_CHART_TOGGLE,
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.PRODUCT_SUMMARY_IS_VISIBLE]: properties => {
      logSessionStreamEvent({
        event_type: 'product_summary_is_visible',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
      })
    },

    [TELEMETRY_EVENT_NAMES.KLARNA_PAYMENTS_MESSAGE_VIEWED]: properties => {
      logSessionStreamEvent({
        event_type: 'is_visible',
        eventId: properties.eventId,
        entity_id: 'klarna_payments',
        entity_type: 'action',
      })
    },

    [TELEMETRY_EVENT_NAMES.KLARNA_PAYMENTS_LINK_CLICKED]: properties => {
      logSessionStreamEvent({
        event_type: 'link_click',
        eventId: properties.eventId,
        entity_id: 'klarna_payments',
        entity_type: 'action',
      })
    },

    [TELEMETRY_EVENT_NAMES.FILTER_APPLIED]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'filter_applied',
        entity_id: properties.action,
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.FILTERS_CLEARED]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'filters_cleared',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.INDIVIDUAL_FILTERS_CLEARED]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'individual_filters_cleared',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.EMPTY_FILTERED_RESULTS]: properties => {
      logSessionStreamEvent({
        event_type: 'empty_filtered_results',
        eventId: properties.eventId,
        context: properties,
      })
    },

    [TELEMETRY_EVENT_NAMES.SUPPORT_CHAT_INITIATED]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'support_chat_initiated',
        eventId: properties.eventId,
        context,
      })
    },

    // B2B Authentication Funnel Events
    [TELEMETRY_EVENT_NAMES.B2B_PAGE_VIEW]: properties => {
      const context = {
        lb_user_id: getlbUserId(),
        email: getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL),
        device_type: getUserDevice(),
        page_name: properties.page_name,
      }

      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.B2B_PAGE_VIEW,
        entity_type: B2B_EVENT_ENTITY_TYPE.B2B_PAGE_VIEW,
        entity_id: properties.storefront_id || 'unknown',
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.B2B_LOGIN_EMAIL_SUBMITTED]: properties => {
      const context = {
        lb_user_id: getlbUserId(),
        email: getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL),
        device_type: getUserDevice(),
        is_whitelisted: properties.is_whitelisted,
      }

      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.B2B_LOGIN_EMAIL_SUBMITTED,
        entity_type: B2B_EVENT_ENTITY_TYPE.B2B_LOGIN_EMAIL_SUBMITTED,
        entity_id: properties.storefront_id || 'unknown',
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.B2B_LOGIN_OTP_SUBMITTED]: properties => {
      const context = {
        lb_user_id: getlbUserId(),
        email: getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL),
        device_type: getUserDevice(),
      }

      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.B2B_LOGIN_OTP_SUBMITTED,
        entity_type: B2B_EVENT_ENTITY_TYPE.B2B_LOGIN_OTP_SUBMITTED,
        entity_id: properties.storefront_id || 'unknown',
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.B2B_LOGIN_SUCCESS]: properties => {
      const context = {
        lb_user_id: getlbUserId(),
        email: getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL),
        device_type: getUserDevice(),
        user_id: properties.user_id,
      }

      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.B2B_LOGIN_SUCCESS,
        entity_type: B2B_EVENT_ENTITY_TYPE.B2B_LOGIN_SUCCESS,
        entity_id: properties.storefront_id || 'unknown',
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.B2B_LOGIN_FAILED]: properties => {
      const context = {
        lb_user_id: getlbUserId(),
        email: getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL),
        device_type: getUserDevice(),
        failure_reason: properties.failure_reason,
      }

      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.B2B_LOGIN_FAILED,
        entity_type: B2B_EVENT_ENTITY_TYPE.B2B_LOGIN_FAILED,
        entity_id: properties.storefront_id || 'unknown',
        context,
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.B2B_ERROR_PAGE]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.B2B_ERROR_PAGE,
        context: properties.context,
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.PRODUCT_FULL_DETAILS_VIEWED]: properties => {
      const context = {
        ...(properties?.context ?? {}),
      }

      logSessionStreamEvent({
        event_type: 'product_full_details_viewed',
        entity_type: 'action',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.DOB_MODAL_OPENED]: properties => {
      const context = {
        status: properties?.status,
      }
      logSessionStreamEvent({
        event_type: DOB_MODAL_EVENT_ENTITY_TYPE.DOB_MODAL_OPENED,
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: DOB_MODAL_EVENT_ENTITY_TYPE.DOB_MODAL_OPENED,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.DOB_ADDED]: properties => {
      const context = {
        status: properties?.status,
      }
      logSessionStreamEvent({
        event_type: DOB_MODAL_EVENT_ENTITY_TYPE.DOB_ADDED,
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: DOB_MODAL_EVENT_ENTITY_TYPE.DOB_ADDED,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.DOB_ADDED_ERROR]: properties => {
      const context = {
        status: properties.status,
      }
      logSessionStreamEvent({
        event_type: DOB_MODAL_EVENT_ENTITY_TYPE.DOB_ADDED_ERROR,
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: DOB_MODAL_EVENT_ENTITY_TYPE.DOB_ADDED_ERROR,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.DOB_REMOVED]: properties => {
      const context = {
        status: properties.status,
      }
      logSessionStreamEvent({
        event_type: DOB_MODAL_EVENT_ENTITY_TYPE.DOB_REMOVED,
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: DOB_MODAL_EVENT_ENTITY_TYPE.DOB_REMOVED,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.ATC_LINK_CLICKED]: properties => {
      const context = {
        ...(properties?.context ?? {}),
      }

      logSessionStreamEvent({
        event_type: 'atc_link_clicked',
        entity_type: 'action',
        eventId: properties.eventId,
        context,
      })
      return
    },

    [TELEMETRY_EVENT_NAMES.HUBSPOT_FORMS_SCRIPT_LOAD_FAILED]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.HUBSPOT_FORMS_SCRIPT_LOAD_FAILED,
        eventId: properties.eventId,
        context: properties,
      })
    },

    [TELEMETRY_EVENT_NAMES.HUBSPOT_LEGACY_FORM_SUBMIT_INITIATED]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.HUBSPOT_LEGACY_FORM_SUBMIT_INITIATED,
        eventId: properties.eventId,
        context: properties,
      })
    },

    [TELEMETRY_EVENT_NAMES.HUBSPOT_FORM_SUBMIT_SUCCESS]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.HUBSPOT_FORM_SUBMIT_SUCCESS,
        eventId: properties.eventId,
        context: properties,
      })
    },

    [TELEMETRY_EVENT_NAMES.HUBSPOT_FORM_SUBMIT_FAILED]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.HUBSPOT_FORM_SUBMIT_FAILED,
        eventId: properties.eventId,
        context: properties,
      })
    },

    [TELEMETRY_EVENT_NAMES.STYLITICS_CTL_CLICK]: properties => {
      const context = {
        ...(properties?.context ?? {}),
      }

      logSessionStreamEvent({
        event_type: 'ctl_clicked',
        entity_type: properties.entity_type,
        entity_id: properties.action,
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.APP_EXCLUSIVE_MODAL_OPENED]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.APP_EXCLUSIVE_MODAL_OPENED,
        eventId: properties.eventId,
        context: { ...properties.context },
      })
    },

    [TELEMETRY_EVENT_NAMES.APP_EXCLUSIVE_LINK_CLICKED]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.APP_EXCLUSIVE_LINK_CLICKED,
        eventId: properties.eventId,
        context: { ...properties.context },
      })
    },

    [TELEMETRY_EVENT_NAMES.LIVE_EVENT_CTA_CLICKED]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.LIVE_EVENT_CTA_CLICKED,
        eventId: properties.eventId,
        context: {
          ctaType: properties.ctaType,
          state: properties.state,
          platform: properties.platform,
          slug: properties.slug,
        },
      })
    },

    [TELEMETRY_EVENT_NAMES.QR_RENDERED_SUCCESS]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.QR_RENDERED_SUCCESS,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.QR_RENDERED_ERROR]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.QR_RENDERED_ERROR,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.ORDER_SEARCH_STARTED]: properties => {
      const context = {
        formData: properties?.formData,
      }
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.ORDER_SEARCH_STARTED,
        eventId: properties.eventId,
        entity_type: TELEMETRY_EVENT_NAMES.ORDER_SEARCH_STARTED,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.ORDER_SEARCH_RESULTS]: properties => {
      const context = {
        formData: properties?.formData,
      }
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.ORDER_SEARCH_RESULTS,
        eventId: properties.eventId,
        entity_type: TELEMETRY_EVENT_NAMES.ORDER_SEARCH_RESULTS,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.FIND_MY_ORDER_WIDGET_CLICKED]: properties => {
      const context = {
        formData: properties?.formData,
      }
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.FIND_MY_ORDER_WIDGET_CLICKED,
        eventId: properties.eventId,
        entity_type: TELEMETRY_EVENT_NAMES.FIND_MY_ORDER_WIDGET_CLICKED,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.INITIATE_OTP_CHECK]: properties => {
      const context = {
        properties: properties?.properties,
      }
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.INITIATE_OTP_CHECK,
        eventId: properties.eventId,
        entity_type: TELEMETRY_EVENT_NAMES.INITIATE_OTP_CHECK,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.OTP_RESEND_ATTEMPT]: properties => {
      const context = {
        properties: properties?.properties,
      }
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.OTP_RESEND_ATTEMPT,
        eventId: properties.eventId,
        entity_type: TELEMETRY_EVENT_NAMES.OTP_RESEND_ATTEMPT,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.OTP_VALIDATE_ATTEMPT]: properties => {
      const context = {
        properties: properties?.properties,
      }
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.OTP_VALIDATE_ATTEMPT,
        eventId: properties.eventId,
        entity_type: TELEMETRY_EVENT_NAMES.OTP_VALIDATE_ATTEMPT,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.SIGN_IN_STARTED]: properties => {
      const context = {
        properties: properties?.properties,
      }
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.SIGN_IN_STARTED,
        eventId: properties.eventId,
        entity_type: TELEMETRY_EVENT_NAMES.SIGN_IN_STARTED,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.FMO_SWITCH_ACCOUNT_CTA_CLICKED]: properties => {
      const context = {
        properties: properties?.properties,
      }
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.FMO_SWITCH_ACCOUNT_CTA_CLICKED,
        eventId: properties.eventId,
        entity_type: TELEMETRY_EVENT_NAMES.FMO_SWITCH_ACCOUNT_CTA_CLICKED,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.LOGOUT]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.LOGOUT,
        eventId: properties.eventId,
        context: properties?.context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CUSTOMIZATION_CHECKBOX_CLICK]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.CUSTOMIZATION_CHECKBOX_CLICK,
        eventId: properties.eventId,
        context: {
          is_selected: properties.isSelected,
          product_id: properties.productId,
        },
      })
    },
    [TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_VIEW]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_VIEW,
        eventId: properties.eventId,
        context: {
          mode: properties.mode,
          product_id: properties.productId,
          variant_quantity_map: properties.variantQuantityMap,
        },
      })
    },
    [TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_LOGO_UPLOAD]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_LOGO_UPLOAD,
        eventId: properties.eventId,
        context: {
          logoUploaded: properties.logoUploaded,
        },
      })
    },
    [TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_NEXT_BUTTON]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_NEXT_BUTTON,
        eventId: properties.eventId,
        context: {
          nextSelected: properties.nextSelected,
        },
      })
    },
    [TELEMETRY_EVENT_NAMES.B2B_DELETE_CUSTOMIZATION_FROM_CART]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.B2B_DELETE_CUSTOMIZATION_FROM_CART,
        entity_type: 'group',
        entity_id: properties.groupId,
        eventId: properties.eventId,
        context: { cartID: getCartIDAndAuth().cartID },
      })
    },

    [TELEMETRY_EVENT_NAMES.B2B_DELETE_CART_GROUP]: properties => {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.B2B_DELETE_CART_GROUP,
        entity_type: 'group',
        entity_id: properties.groupId,
        eventId: properties.eventId,
        context: {
          cartID: getCartIDAndAuth().cartID,
          group_id: properties.groupId,
          ...(properties.variantId != null ? { variant_id: String(properties.variantId) } : {}),
          ...(properties.productId != null ? { product_id: String(properties.productId) } : {}),
        },
      })
    },

    [TELEMETRY_EVENT_NAMES.OTP_SIGN_IN_MODAL_OPEN]: properties => {
      logSessionStreamEvent({
        event_type: 'otp_sign_in_modal_open',
        entity_type: 'email',
        entity_id: properties.email,
        context: { email: properties.email },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.OTP_SIGN_IN_SUCCESS]: properties => {
      logSessionStreamEvent({
        event_type: 'otp_sign_in_success',
        entity_type: 'email',
        entity_id: properties.email,
        context: { email: properties.email },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.OTP_SIGN_IN_FAILURE]: properties => {
      logSessionStreamEvent({
        event_type: 'otp_sign_in_failure',
        entity_type: 'email',
        entity_id: properties.email,
        context: { email: properties.email, error: properties.error },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.OTP_SIGN_IN_RESEND]: properties => {
      logSessionStreamEvent({
        event_type: 'otp_sign_in_resend',
        entity_type: 'email',
        entity_id: properties.email,
        context: { email: properties.email },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.ADD_PHOTO_CLICKED]: properties => {
      logSessionStreamEvent({
        event_type: 'add_photo_clicked',
        entity_type: 'action',
        entity_id: properties.eventId,
        context: properties.context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.REVIEW_IMAGE_UPLOADED]: properties => {
      logSessionStreamEvent({
        event_type: 'review_image_uploaded',
        entity_type: 'action',
        entity_id: properties.eventId,
        context: properties.context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.RATING_ADDED]: properties => {
      logSessionStreamEvent({
        event_type: 'rating_added',
        entity_type: 'click',
        entity_id: properties.eventId,
        context: properties.context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.REVIEW_DETAILS_ADDED]: properties => {
      logSessionStreamEvent({
        event_type: 'review_details_added',
        entity_type: 'action',
        entity_id: properties.eventId,
        context: properties.context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.REVIEW_DETAILS_SUBMITTED]: properties => {
      logSessionStreamEvent({
        event_type: 'review_details_submitted',
        entity_type: 'click',
        entity_id: properties.eventId,
        context: properties.context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.REVIEW_SUBMISSION_ERROR_LOG]: properties => {
      logSessionStreamEvent({
        event_type: 'review_submission_error_log',
        entity_type: 'action',
        entity_id: properties.eventId,
        context: properties.context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.EARN_CREDIT_VIEWED]: properties => {
      const context = {
        ...(properties?.context ?? {}),
        product_id: properties.productId,
        isValentineProduct: properties.isValentineProduct,
        name: properties.productTitle,
        division: properties.division,
        subDepartment: properties.subDepartment,
        department: properties.department,
        gender: properties.gender,
      }

      logSessionStreamEvent({
        event_type: 'is_visible',
        entity_type: 'action',
        entity_id: 'quince_credit',
        context,
        eventId: properties.eventId,
      })
      return
    },
    [TELEMETRY_EVENT_NAMES.BEST_MARKET_VALUE_VIEWED]: properties => {
      const context = {
        ...(properties?.context ?? {}),
        product_id: properties.productId,
        isValentineProduct: properties.isValentineProduct,
        name: properties.productTitle,
        division: properties.division,
        subDepartment: properties.subDepartment,
        department: properties.department,
        gender: properties.gender,
      }

      logSessionStreamEvent({
        event_type: 'is_visible',
        entity_type: 'action',
        entity_id: 'best_market_value',
        context,
        eventId: properties.eventId,
      })
      return
    },

    [TELEMETRY_EVENT_NAMES.FCP_EVENT]: properties => {
      logSessionStreamEvent({
        event_type: 'performance_tracker',
        entity_type: 'fcp_event',
        entity_id: 'duration',
        context: { fcp: properties.fcp },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.LCP_EVENT]: properties => {
      logSessionStreamEvent({
        event_type: 'performance_tracker',
        entity_type: 'lcp_event',
        entity_id: 'duration',
        context: { lcp: properties.lcp },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.CLS_EVENT]: properties => {
      logSessionStreamEvent({
        event_type: 'performance_tracker',
        entity_type: 'cls_event',
        entity_id: 'percentage',
        context: { cls: properties.cls },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.INP_EVENT]: properties => {
      logSessionStreamEvent({
        event_type: 'performance_tracker',
        entity_type: 'inp_event',
        entity_id: 'duration',
        context: { inp: properties.inp },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.TTFB_EVENT]: properties => {
      logSessionStreamEvent({
        event_type: 'performance_tracker',
        entity_type: 'ttfb_event',
        entity_id: 'duration',
        context: { ttfb: properties.ttfb },
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.FINGERPRINT_REQUESTED]: properties => {
      logSessionStreamEvent({
        event_type: 'qfp_visitor_id_requested',
        entity_type: 'tracking',
        entity_id: 'qfp',
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.FINGERPRINT_RECEIVED]: properties => {
      logSessionStreamEvent({
        event_type: 'qfp_visitor_id_received',
        entity_type: 'tracking',
        entity_id: 'qfp',
        eventId: properties.eventId,
      })
    },
  },
}
// Note: We will now no longer have cart details in any event as we dont get this info from checkout graphql api
export const logSessionStreamEvent = ({
  event_type,
  entity_type,
  entity_id,
  context,
  cart_details,
  eventId,
  pageUrl,
}: {
  event_type: any
  entity_type?: any
  entity_id?: any
  context?: any
  cart_details?: any
  eventId: string
  pageUrl?: string
}) => {
  cart_details = cart_details || {}
  const { context: appContextData, ...appRestEventData } = getAppAnalyticsData()
  const featureGates = getUserAgnosticGlobalFeatureGatesFromMemory()
  context = {
    ...appContextData,
    ...context,
    isPartOfNewSiteLaunchExperiment: getCookie(ANALYTIC_COOKIES.IS_PART_OF_NEW_SITE_LAUNCH_EXPERIMENT),
    bucketForNewSiteLaunchExperiment: getCookie(ANALYTIC_COOKIES.BUCKET_FOR_NEW_SITE_LAUNCH_EXPERIMENT),
  }
  let email = getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ?? getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL)
  if (email) {
    email = email.toLowerCase()
  }

  // STRFRNT-15281: any email a logged-out user *explicitly types into a form* is consented
  // first-party data and must flow to DE, same as US — newsletter/email signup,
  // join-the-waitlist, email (OTP) sign-in, and the referral invite (the user typed that
  // address too). These events are exempt from the UK logged-out PII strip below, which
  // targets only the *residual* cookie-identity email auto-attached to unrelated events.
  const EXPLICIT_EMAIL_OPT_IN_EVENTS = [
    'join_the_waitlist',
    'join_the_waitlist_sms',
    'klaviyo_newsletter_subscribe',
    'otp_sign_in_modal_open',
    'otp_sign_in_success',
    'otp_sign_in_failure',
    'otp_sign_in_resend',
    'send_referral_email_invite',
  ]
  const isExplicitEmailOptInEvent = EXPLICIT_EMAIL_OPT_IN_EVENTS.includes(event_type)

  // The `form_submit` event is generic (all forms share the event name), so for the
  // typed-email form actions we preserve the address by `context.action` rather than by
  // event_type. Audited against the FORM_SUBMIT action union in telemetry-events.types.ts:
  //   - account_login / account_create               → login + registration forms
  //   - forgot_password / resend_verification_forgot_password
  //     / click_reset_password / click_reset_password_unverified
  //                                                  → password-reset flow (typed email)
  //   - COMING_SOON_JOIN_US                          → back-in-stock email signup
  //   - TRADE_APPLICATION_SUBMISSION                 → B2B trade-app form (email in spread formValues)
  //   - successful_review_submission                 → product review: the reviewer typed their
  //     own email (context.email + nested context.requestForStamped.email) — manually entered,
  //     so it flows in context like the other typed-email events.
  // Other FORM_SUBMIT actions (account_logout, send_invite click, submit_review_click) carry no
  // typed email, so they fall through to the strip below.
  const TYPED_EMAIL_FORM_ACTIONS = new Set<string>([
    'account_login',
    'account_create',
    'forgot_password',
    'resend_verification_forgot_password',
    'click_reset_password',
    'click_reset_password_unverified',
    'COMING_SOON_JOIN_US',
    'successful_review_submission',
    'TRADE_APPLICATION_SUBMISSION',
  ])
  const ctxAction = (context as Record<string, unknown>)?.action
  const isTypedEmailFormSubmit = typeof ctxAction === 'string' && TYPED_EMAIL_FORM_ACTIONS.has(ctxAction)

  // Some typed-email events are keyed by `entity_id` rather than event_type / context.action.
  // The Attentive popup submission fires as a generic `button_click` (no context.action) — mark
  // it by entity_id so the UK logged-out strip below is a no-op for it. Today this is defensive
  // only (UK: LB_CUSTOMER_EMAIL is erased on logout and LB_CUSTOMER_TENTATIVE_EMAIL is never
  // persisted, so `email` is already undefined and `context.email` is absent — nothing to strip).
  // It ensures the event survives untouched if either upstream assumption ever changes.
  const TYPED_EMAIL_ENTITY_IDS = new Set<string>(['attentive_email_submitted'])
  const isTypedEmailEntity = typeof entity_id === 'string' && TYPED_EMAIL_ENTITY_IDS.has(entity_id)
  // Any event where the user explicitly typed an email this submission — opt-in events
  // above, or a form-submit action listed above. For these the typed email is kept in
  // `context` (and the email `entity_id`) so it still reaches DE; only NON-typed events
  // get the residual email stripped. We don't persist it anywhere for UK — the event
  // payload IS the delivery (root-level `email` stays empty unless a logged-in identity
  // cookie exists).
  const isExplicitlyTypedEmailEvent = isExplicitEmailOptInEvent || isTypedEmailFormSubmit || isTypedEmailEntity

  // UK (GDPR): a logged-in customer's email MAY flow to the DE ingestion stream until they
  // log out — on logout the Cognito session ends (AuthUtil.isLoggedIn() → false) and
  // LB_CUSTOMER_EMAIL is erased, so once logged out there is no logged-in identity to
  // attach. For a logged-OUT UK visitor we strip only the *residual* cookie-identity email
  // auto-attached to the event (top-level `email`, and any email keys spread into `context`)
  // — but NOT an email the user explicitly typed on this event. US/CA unaffected.
  if (getIsCountryCompliant() && !AuthUtil.isLoggedIn() && !isExplicitlyTypedEmailEvent) {
    email = undefined
    const ctx = context as Record<string, unknown>
    delete ctx.email
    delete ctx.login_email
    if (entity_type === 'email') {
      entity_id = undefined
    }
  }

  const currentDomain = `${location.origin}/`

  const { currentPage, previousPage } = getPageContextDetails()

  validateLbUserId()
  validateSessionId()

  const localisationData = getLocalisationDataFromStorage()
  // When `mixpanel_region_fix` is enabled, always emit country/currency/language/locale.
  // A subset of events fire before the locale context writes REGION_DETAILS to
  // localStorage; for those, `getLocalisationDataWithFallbacks` resolves the country from
  // the server-set locale cookie (preserving the real country) before falling back to
  // defaults, so `country` stays populated and Mixpanel's derived `region_code` is no
  // longer left unset. When the gate is off we retain the exact legacy behavior (drop the
  // localisation fields entirely if storage is empty) for a clean, deploy-free rollback.
  const fallbackLocalisation = featureGates?.mixpanel_region_fix
    ? getLocalisationDataWithFallbacks(localisationData, { useLocaleCookieFallback: true })
    : null
  const localisationMetadata = fallbackLocalisation
    ? {
        country: fallbackLocalisation.country,
        currency: fallbackLocalisation.currency,
        language: fallbackLocalisation.language,
        browser_language: navigator.language,
        region_id: fallbackLocalisation.regionId,
        locale: fallbackLocalisation.locale,
      }
    : localisationData
      ? {
          country: localisationData.country,
          currency: localisationData.currency,
          language: localisationData.language,
          browser_language: navigator.language,
          region_id: localisationData.regionId,
          locale: localisationData.locale,
        }
      : {}
  const { utm_source, utm_medium, utm_campaign, utm_content, utm_term, qclid } = getUtmParameters()
  const event_data = {
    ...appRestEventData,
    ...localisationMetadata,
    id: eventId,
    event_type: event_type,
    entity_type: entity_type,
    entity_id: entity_id,
    context: {
      ...context,
      ...getStorefrontIdProperty(), //storefront_id is used to identify the b2b self serve flow
    },
    session_id: getSessionId(),
    lb_user_id: getlbUserId(),
    lb_visitor_details: getFingerprintVisitorDetails(),
    q_cookie_id: getCookie(ANALYTIC_COOKIES.Q_COOKIE_ID),
    email: email,
    device: getUserDevice(),
    device_category: getUserDeviceCategory(),
    url: pageUrl ?? getSafeLocationHref(),
    // Cookie-consent state, sent on every DE event for all regions. Reflects the
    // TrustArc decision persisted in the consent cookies (absent cookie => false).
    cookie_consent: {
      advertising_cookie_accepted: getCookie(USER_CONSENT_COOKIES.ADVERTISING) === 'true',
      functional_cookie_accepted: getCookie(USER_CONSENT_COOKIES.FUNCTIONAL) === 'true',
    },
    campaign: {
      source: utm_source,
      medium: utm_medium,
      campaign: utm_campaign,
      content: utm_content,
      term: utm_term,
    },
    thirdPartyAnalyticsData: {
      metaPixel: {
        fbc: getCookie(ANALYTIC_COOKIES.THIRD_PARTY.FACEBOOK_FBC),
        fbp: getCookie(ANALYTIC_COOKIES.THIRD_PARTY.FACEBOOK_FBP),
        external_id: getlbUserId(),
      },
      google: {
        ga_id: getCookie(ANALYTIC_COOKIES.THIRD_PARTY.GOOGLE_GA_ID),
      },
      tiktok: {
        ttp: getCookie(ANALYTIC_COOKIES.THIRD_PARTY.TIKTOK_PIXEL),
      },
    },
    event_time: new Date(),
    cart_token: cart_details.token,
    is_logged_in: AuthUtil.isLoggedIn(),
    active_experiments: getActiveExperiments(),
    referrer: isBrowser() ? (window.__client_side_navigation__ ? currentDomain : document.referrer) : '',
    qclid,
    app_environment_type: appData.environmentType.toUpperCase(),
    page_name: currentPage.page_name,
    page_type: currentPage.page_type,
    page_path: currentPage.page_path,
    ...(currentPage?.page_variant && { page_variant: currentPage.page_variant }),
    page_variant: currentPage.page_variant,
    previous_page_name: previousPage.page_name,
    previous_page_type: previousPage.page_type,
    previous_page_path: previousPage.page_path,
    ...(previousPage?.page_variant && { previous_page_variant: previousPage.page_variant }),
    zip_code: getZipCodeFromCookie(false) ?? undefined,
    customer_id: getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_ID),
    server_locale: getCookie(LOCALE_COOKIE) ?? DEFAULT_LOCALE,
  }
  const event_data_v2_payload = {
    event: 'ia_event',
    event_server_ts: Date.now(),
    data: [{ ...event_data, event_time: Date.now(), is_ia_v2_event: true }],
  }

  if (CONFIG_DEBUG_EVENT_LOGGING && isBrowser()) {
    // eslint-disable-next-line i18next/no-literal-string
    console.info('Internal Analytics', event_data)
    // eslint-disable-next-line i18next/no-literal-string
    console.info('Internal Analytics V2', event_data_v2_payload)
  }
  executeFireAndForgotTask(() => {
    fetch(CONFIG_INTERNAL_ANALYTICS_V2_ENDPOINT, {
      method: 'POST',
      mode: 'cors',
      credentials: 'same-origin',
      headers: {
        'Content-type': 'application/json',
      },
      redirect: 'follow',
      referrer: 'no-referrer',
      body: JSON.stringify(event_data_v2_payload),
    }).catch((err: unknown) => {
      const quinceError = new QuinceApiError({
        error: err as Error,
        message: 'InternalAnalytics V2 api failed',
        apiName: 'InternalAnalytics V2 track',
      })
      captureException(quinceError, {
        feature: FEATURES.TRACK_EVENT,
        module: MODULES.INTERNAL_ANALYTICS,
        extraInfo: {
          capturedFrom: 'logSessionStreamEvent in InternalAnalytics V2',
        },
      })
    })
    trackWithSgtm({ eventData: event_data }).catch((err: unknown) => {
      throw err
    })
    // mixpanelCl.track(event_data)
    mixpanelCl.trackWithSgtm(event_data)
  }, EVENT_DELAY)
}

// Persists an email collected pre-login (newsletter, waitlist, referral form,
// login form-submit, etc.) into the LB_CUSTOMER_TENTATIVE_EMAIL cookie so that
// downstream pixel integrations (FbPixel, ServerSGTMPixel, edge-tag, …) can
// forward hashed PII for ad-network event matching even before the user has
// completed authentication. No-op when LB_CUSTOMER_EMAIL is already set
// (logged-in user) or the input is not a valid email.
const captureTentativeEmail = (email: string, isTentativeEmail = true) => {
  const emailCookie = getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL)
  if (!emailCookie && email && validateEmail(email)) {
    // UK (countryCompliant): never persist raw email to a cookie. US/CA unchanged.
    if (!getIsCountryCompliant()) {
      setCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL, email)
    }
    // US/CA: identify the tentative email in Mixpanel (unchanged from develop).
    // UK: mixpanelCl.identify is a no-op — the browser SDK is disabled via
    // isSdkDisabled() (ssr_mixpanel_sdk_enabled=false), so no PII reaches Mixpanel.
    mixpanelCl.identify({ email, isTentativeEmail })
  }
}

// Mimicking what is done in the render code of collection page.
const getVariantLocationInCollectionOnDesktop = (index: number) => {
  if (index < 3) {
    return { row: 0, col: index }
  }
  if (index < 7) {
    return { row: 1, col: index - 3 }
  }
  if (index === 7) {
    return { row: 3, col: 0 }
  }
  if (index < 12) {
    return { row: 4, col: index - 8 }
  }
  if (index < 14) {
    return { row: 5, col: index - 12 }
  }
  if (index < 17) {
    return { row: 6, col: index - 14 }
  }
  const loadMoreIndex = index - 17
  const block = parseInt(loadMoreIndex / 7)
  const blockIndex = loadMoreIndex % 7
  const row = 6 + block * 2 + (blockIndex < 4 ? 1 : 2)
  const col = blockIndex < 4 ? blockIndex : blockIndex - 4
  return {
    row,
    col,
  }
}

// Mimicking what is done in the render code of collection page.
const getVariantLocationInCollectionOnMobile = (index: number) => {
  const block = parseInt(index / 3)
  const blockIndex = index % 3
  const row = block * 2 + (blockIndex < 2 ? 0 : 1)
  const col = blockIndex < 2 ? blockIndex : blockIndex - 2
  return {
    row,
    col,
  }
}

export const trackWebsiteAction = ({ actionName, payload = {} }: { actionName: string; payload?: object }) => {
  try {
    logSessionStreamEvent({
      event_type: 'website_action',
      entity_type: 'action_name',
      entity_id: actionName,
      context: payload,
      eventId: '',
    })
  } catch (e) {
    captureException(new Error(`Error tracking website action`), {
      module: MODULES.INTERNAL_ANALYTICS,
      feature: FEATURES.TRACK_EVENT,
    })
  }
}
